import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import React, { FC, useState } from "react";
import styled from "styled-components";
import { Link } from "components";
import { Fund } from "components/base/TableFunds/types";
import { Close } from "icons/Close";
import { Modal } from "./Modal";
import { Table } from "./Table";
import { transformDate } from "./transformDate";

type Props = {
  fund: Fund | undefined;
  isOpen: boolean;
  onRequestClose: () => void;
};

export const ModalFunds: FC<Props> = withErrorBoundary(({ fund, isOpen, onRequestClose }) => {
  const [isSticky, setIsSticky] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget;

    setIsSticky(scrollTop > 0);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} onScroll={handleScroll}>
      <StyledFundsModal>
        <div className={cn("FundsModal__Header", { FundsModal__Shadow: isSticky })}>
          <h3 className="FundsModal__title Font_24_32_600">{fund?.name}</h3>
          <button className="FundsModal__CloseButton" type="button" onClick={onRequestClose}>
            <Close className="FundsModal__CloseIcon" />
          </button>
        </div>

        <div className="FundsModal__Body">
          <div className="FundsModal__Text">
            <p className="FundsModal__description Color_secondary Font_16_22_450">{fund?.description}</p>
          </div>

          <div className="FundsModal__Table__Container">
            <Table fund={fund} className="FundsModal__Table" />
            {fund?.external_link && (
              <Link
                href={fund.external_link}
                className="FundsModal__Table__ExternalLink Color_secondary Font_14_20_450"
              >
                View the full profile on {fund?.link_site_name}
              </Link>
            )}
            <span className="FundsModal__Table__UpdateDate Color_secondary Font_12_16_450">
              Last updated: {fund && transformDate(fund.updated_at)}
            </span>
          </div>
        </div>
      </StyledFundsModal>
    </Modal>
  );
}, {});

const StyledFundsModal = styled.div`
  .FundsModal__Header {
    position: sticky;
    top: 0;
    width: 100%;
    display: flex;
    margin-top: 24px;
    justify-content: space-between;
    align-items: center;
    padding: 16px 40px;
    background: white;
    margin-bottom: 8px;
    transition: 0.1s box-shadow ease;
    z-index: 1;
  }

  .FundsModal__Shadow {
    box-shadow: 0 1px 1px rgb(0 0 0 / 12%);
  }

  .FundsModal__CloseButton {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: -8px;
    padding: 8px;
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }

  .FundsModal__CloseIcon {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.fog["80"]};
  }

  .FundsModal__Body {
    padding: 0 40px;
    display: flex;
    gap: 64px;
    margin-bottom: 40px;
  }

  .FundsModal__Table {
    width: 100%;
  }

  .FundsModal__Table__Container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1 0 45%;
  }

  .FundsModal__Table__ExternalLink {
    cursor: pointer;
    text-decoration: underline;
    align-self: flex-end;

    &:hover {
      text-decoration: none;
    }
  }

  .FundsModal__Table__UpdateDate {
    text-align: right;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .FundsModal__Header {
      margin-top: 16px;
      justify-content: space-between;
      align-items: center;
      padding: 8px 24px;
      background: white;
      margin-bottom: 16px;
      transition: 0.1s box-shadow ease;
    }

    .FundsModal__Body {
      padding: 0 24px;
      margin-bottom: 24px;
      gap: 24px;
      flex-direction: column-reverse;
    }

    .FundsModal__Table__ExternalLink {
      align-self: flex-start;
    }

    .FundsModal__Table__UpdateDate {
      text-align: left;
    }
  }
`;
